import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import { createProduct, fetchProduct, updateProduct, getProducts } from '../../../api/ProductsAPI';
import { getUoms } from '../../../api/UomsAPI';
import { handleApiError, getBaseUrl } from '../../../api/risapi';
import AppToaster from '../../AppToaster';
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions';
import ImageCropperModal from '../../common/ImageCropperModal';

const ProductsForm = ({ formType, currentProductId }) => {
    const navigate = useNavigate();

    // --- State Management ---
    const [currentProduct, setCurrentProduct] = useState({
        name: '',
        barcode:'',
        code: '',
        base_uom_id: null,
        product_units: [], // Array for alternate units
        allow_negative_stock: false,
        image: null, // Stores the file object for upload
    });

    const [imagePreview, setImagePreview] = useState(null); // Stores URL for preview
    const [currentProductError, setCurrentProductError] = useState({});
    const [uoms, setUoms] = useState([]); // For dropdowns
    const [productNumbering, setProductNumbering] = useState('auto');
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [loading, setLoading] = useState(false);

    // --- Cropper State ---
    const [showCropper, setShowCropper] = useState(false);
    const [cropperSrc, setCropperSrc] = useState(null);

    // --- Fetch UoMs ---
    useEffect(() => {
        setLoading(true);
        getUoms({ paginate: false }, (response) => {
            setLoading(false);
            if (response.uoms) {
                if (Array.isArray(response.uoms)) {
                    setUoms(response.uoms.map(uom => ({ 
                        value: uom.id, 
                        label: `${uom.name} (${uom.short_name})` 
                    })));
                } else {
                    setUoms([]);
                }
            }
        });
    }, []);

    // --- Fetch Product Data & Settings ---
    useEffect(() => {
        // Reset State
        setCurrentProduct({ 
            name: '', 
            barcode:'',
            code: '', 
            base_uom_id: null, 
            product_units: [], 
            allow_negative_stock: false, 
            image: null 
        });
        setImagePreview(null);
        setCurrentProductError({});
        setLoading(true);

        if (formType === 'edit' && currentProductId) {
            fetchProduct(currentProductId, (response) => {
                setLoading(false);
                if (response.product) {
                    const fetchedProduct = response.product;
                    const alternateUnits = Array.isArray(fetchedProduct.product_units)
                        ? fetchedProduct.product_units
                        : [];

                    setCurrentProduct({
                        ...fetchedProduct,
                        product_units: alternateUnits.map(unit => ({
                            id: unit.id,
                            uom_id: unit.uom_id,
                            conversion_factor: unit.conversion_factor,
                            uom_label: unit.uom ? `${unit.uom.name} (${unit.uom.short_name})` : 'Unknown Unit'
                        })),
                        allow_negative_stock: !!fetchedProduct.allow_negative_stock,
                        image: null // Reset file input
                    });

                    // Set existing image preview if available
                    if (fetchedProduct.image_path) {
                        setImagePreview(`${getBaseUrl()}/storage/${fetchedProduct.image_path}`);
                    }

                    setProductNumbering(response.product_numbering || 'auto');
                } else {
                    handleApiError(response, setCurrentProductError);
                }
            });
        } else if (formType === 'create') {
            getProducts({ paginate: false, limit: 1 }, (response) => {
                setLoading(false);
                setProductNumbering(response.product_numbering || 'auto');
            });
        } else {
            setLoading(false);
        }
    }, [formType, currentProductId]);

    // --- Input Handlers ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const inputValue = type === 'checkbox' ? checked : value;
        setCurrentProduct(prev => ({ 
            ...prev, 
            [name]: inputValue 
        }));
    };

    const handleBaseUomChange = (selectedOption) => {
        setCurrentProduct(prev => ({ 
            ...prev, 
            base_uom_id: selectedOption ? selectedOption.value : null 
        }));
    };

    // --- Image Selection & Cropping Handlers ---
    const handleImageSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setCropperSrc(reader.result); // Set source for cropper
                setShowCropper(true);         // Open modal
            });
            reader.readAsDataURL(file);
            // Clear input value to allow selecting the same file again if needed
            e.target.value = null; 
        }
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        setCurrentProduct(prev => ({ ...prev, image: croppedFile })); // Store the file object
        setImagePreview(previewUrl); // Show the local preview
    };

    // --- Alternate Unit Handlers ---
    const addProductUnit = () => {
        setCurrentProduct(prev => ({
            ...prev,
            product_units: [
                ...(Array.isArray(prev.product_units) ? prev.product_units : []),
                { id: null, uom_id: null, conversion_factor: '' }
            ],
        }));
    };

    const removeProductUnit = (index) => {
        setCurrentProduct(prev => ({
            ...prev,
            product_units: (Array.isArray(prev.product_units) ? prev.product_units : []).filter((_, i) => i !== index),
        }));
    };

    const handleProductUnitChange = (index, fieldName, value) => {
        setCurrentProduct(prev => {
            const currentUnits = Array.isArray(prev.product_units) ? prev.product_units : [];
            const newUnits = [...currentUnits];
            if (!newUnits[index]) return prev;

            newUnits[index] = { ...newUnits[index], [fieldName]: value };
            return { ...prev, product_units: newUnits };
        });
    };

    // --- Save Handler ---
    const saveForm = (exitForm) => {
        setCurrentProductError({});
        setLoading(true);
        const apiCall = formType === 'create' ? createProduct : updateProduct;

        // Prepare data
        // Note: convertJsonToFormData in ProductsAPI will handle the 'image' file
        const dataToSend = {
            ...currentProduct,
            allow_negative_stock: currentProduct.allow_negative_stock ? 1 : 0,
            product_units: (Array.isArray(currentProduct.product_units) ? currentProduct.product_units : []).map(unit => ({
                id: unit.id,
                uom_id: unit.uom_id,
                conversion_factor: unit.conversion_factor
            }))
        };

        apiCall(dataToSend, (response) => {
            setLoading(false);
            if (response.product) {
                if (exitForm) {
                    navigate('/franchisee_user/masters/products', { 
                        state: { message: `Product ${formType === 'create' ? 'saved' : 'updated'} successfully!` } 
                    });
                } else {
                    if (formType === 'create') {
                        // Reset for next entry
                        setCurrentProduct({ 
                            name: '', 
                            barcode:'',
                            code: '', 
                            base_uom_id: null, 
                            product_units: [], 
                            allow_negative_stock: false, 
                            image: null 
                        });
                        setImagePreview(null);
                        setToaster({ type: 'success', heading: 'Success', message: 'Product added successfully' });
                    } else {
                        // Update state with response data
                        const updatedUnits = Array.isArray(response.product.product_units) ? response.product.product_units : [];
                        setCurrentProduct(prev => ({
                            ...prev,
                            ...response.product,
                            allow_negative_stock: !!response.product.allow_negative_stock,
                            image: null,
                            product_units: updatedUnits.map(unit => ({
                                id: unit.id,
                                uom_id: unit.uom_id,
                                conversion_factor: unit.conversion_factor,
                                uom_label: unit.uom ? `${unit.uom.name} (${unit.uom.short_name})` : 'Unknown'
                            }))
                        }));

                        if (response.product.image_path) {
                            setImagePreview(`${getBaseUrl()}/storage/${response.product.image_path}`);
                        }
                        setToaster({ type: 'success', heading: 'Success', message: 'Product updated successfully' });
                    }
                    setShowToaster(true);
                }
            } else {
                handleApiError(response, setCurrentProductError);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not save product.' });
                setShowToaster(true);
            }
        });
    };

    // --- Reset Handler ---
    const handleReset = () => {
        if (formType === 'create') {
            setCurrentProduct({ 
                name: '', 
                barcode:'',
                code: '', 
                base_uom_id: null, 
                product_units: [], 
                allow_negative_stock: false, 
                image: null 
            });
            setImagePreview(null);
            setCurrentProductError({});
        } else {
            setLoading(true);
            fetchProduct(currentProductId, (response) => {
                setLoading(false);
                if (response.product) {
                    const fetchedProduct = response.product;
                    const alternateUnits = Array.isArray(fetchedProduct.product_units) ? fetchedProduct.product_units : [];
                    setCurrentProduct({
                        ...fetchedProduct,
                        allow_negative_stock: !!fetchedProduct.allow_negative_stock,
                        image: null,
                        product_units: alternateUnits.map(unit => ({
                            id: unit.id,
                            uom_id: unit.uom_id,
                            conversion_factor: unit.conversion_factor,
                            uom_label: unit.uom ? `${unit.uom.name} (${unit.uom.short_name})` : 'Unknown'
                        })),
                    });
                    if (fetchedProduct.image_path) {
                        setImagePreview(`${getBaseUrl()}/storage/${fetchedProduct.image_path}`);
                    } else {
                        setImagePreview(null);
                    }
                    setCurrentProductError({});
                }
            });
        }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            
            {/* Image Cropper Modal */}
            <ImageCropperModal 
                show={showCropper} 
                handleClose={() => setShowCropper(false)} 
                imageSrc={cropperSrc}
                onCropComplete={handleCropComplete}
            />

            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title={`${capitalizeFirstLetter(formType)} Product`}>
                         <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                         <li><a className="f-s-14 f-w-500" href="/franchisee_user/masters/products">Products</a></li>
                         <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                    </AppBreadCrumb>

                    {loading && <div className="loader_box"><div className="loader_14"></div></div>}

                    {!loading && (
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5>{formType === 'create' ? 'Add New' : 'Edit'} Product {formType === 'edit' ? `: ${currentProduct?.name || ''}` : ''}</h5>
                                    </div>
                                    <div className="card-body">
                                        <form className="app-form" onSubmit={(e) => e.preventDefault()}>
                                            
                                            {/* --- Product Details Section --- */}
                                            <h6 className="mb-3">Product Details</h6>
                                            <div className="row mb-3">
                                                
                                                {/* Image Upload Column */}
                                                <div className="col-md-3 mb-3 text-center">
                                                    <label className="form-label d-block text-start">Product Image (1:1)</label>
                                                    <div 
                                                        className="border d-flex align-items-center justify-content-center bg-light rounded" 
                                                        style={{ height: '150px', overflow:'hidden', cursor:'pointer' }} 
                                                        onClick={()=> document.getElementById('productImageInput').click()}
                                                    >
                                                        {imagePreview ? (
                                                            <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                        ) : (
                                                            <div className="text-secondary">
                                                                <i className="ti ti-camera f-s-24"></i>
                                                                <div className="f-s-12">Click to Upload & Crop</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <input 
                                                        type="file" 
                                                        id="productImageInput" 
                                                        className="d-none" 
                                                        accept="image/*"
                                                        onChange={handleImageSelect}
                                                    />
                                                    <span className="text-danger d-block text-start">{currentProductError?.image}</span>
                                                </div>

                                                {/* Text Inputs Column */}
                                                <div className="col-md-9">
                                                    <div className="row">
                                                        {/* Name */}
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Name *</label>
                                                            <input 
                                                                className="form-control" 
                                                                name="name" 
                                                                value={currentProduct.name || ''} 
                                                                onChange={handleInputChange} 
                                                                required 
                                                            />
                                                            <span className="text-danger">{currentProductError?.name}</span>
                                                        </div>
                                                        
                                                        {/* Code (Manual) */}
                                                        {productNumbering === 'manual' && (
                                                            <div className="col-md-6 mb-3">
                                                                <label className="form-label">Code *</label>
                                                                <input 
                                                                    className="form-control" 
                                                                    name="code" 
                                                                    value={currentProduct.code || ''} 
                                                                    onChange={handleInputChange} 
                                                                    required 
                                                                />
                                                                <span className="text-danger">{currentProductError?.code}</span>
                                                            </div>
                                                        )}
                                                        {/* Code (Auto - Edit Mode) */}
                                                        {productNumbering === 'auto' && formType === 'edit' && (
                                                            <div className="col-md-6 mb-3">
                                                                <label className="form-label">Code</label>
                                                                <input 
                                                                    className="form-control" 
                                                                    value={currentProduct.code || ''} 
                                                                    readOnly 
                                                                    disabled 
                                                                />
                                                            </div>
                                                        )}


                                                         {/* Bat Code */}
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Bar Code</label>
                                                            <input 
                                                                className="form-control" 
                                                                name="barcode" 
                                                                value={currentProduct.barcode || ''}
                                                                placeholder='Enter Bar code or Scan bar code' 
                                                                onChange={handleInputChange} 
                                                                 
                                                            />
                                                            <span className="text-danger">{currentProductError?.name}</span>
                                                        </div>



                                                        {/* Base Unit */}
                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Base Unit (Selling Unit) *</label>
                                                            <Select
                                                                options={uoms}
                                                                value={uoms.find(option => option.value === currentProduct.base_uom_id)}
                                                                onChange={handleBaseUomChange}
                                                                placeholder="Select Base UoM"
                                                                isClearable
                                                            />
                                                            <span className="text-danger">{currentProductError?.base_uom_id}</span>
                                                        </div>

                                                        {/* Negative Stock Toggle */}
                                                        {/* <div className="col-md-6 mb-3 d-flex align-items-center pt-4">
                                                            <div className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    id="allow_negative_stock"
                                                                    name="allow_negative_stock"
                                                                    checked={currentProduct.allow_negative_stock || false}
                                                                    onChange={handleInputChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="allow_negative_stock">
                                                                    Allow Negative Stock?
                                                                </label>
                                                            </div>
                                                        </div> */}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* --- Alternate Units Section --- */}
                                            <hr />
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">Alternate / Packaging Units</h6>
                                                <button type="button" className="btn btn-sm google-blue-button" onClick={addProductUnit}>
                                                    <i className="ti ti-plus me-1"></i> Add Unit
                                                </button>
                                            </div>
                                            
                                            {/* General error for the units array */}
                                             <span className="text-danger d-block mb-2">
                                                 {currentProductError?.product_units && typeof currentProductError.product_units === 'string'
                                                     ? currentProductError.product_units
                                                     : ''}
                                            </span>

                                            {/* Mapping Alternate Units */}
                                            {currentProduct && Array.isArray(currentProduct.product_units) && currentProduct.product_units.map((unit, index) => (
                                                <div key={unit.id || `unit-${index}`} className="unit-block border p-3 mb-3 rounded position-relative bg-light">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1" 
                                                        onClick={() => removeProductUnit(index)} 
                                                        style={{ lineHeight: 1, padding: '0.2rem 0.4rem' }} 
                                                        title="Remove Unit"
                                                    >
                                                        &times;
                                                    </button>
                                                    <div className="row align-items-end">
                                                        {/* Unit Name Dropdown */}
                                                        <div className="col-md-5 mb-3">
                                                            <label className="form-label">Unit Name *</label>
                                                            <Select
                                                                options={uoms.filter(uom => uom.value !== currentProduct.base_uom_id)} // Exclude base UoM
                                                                value={uoms.find(option => option.value === unit.uom_id)}
                                                                onChange={(selectedOption) => handleProductUnitChange(index, 'uom_id', selectedOption ? selectedOption.value : null)}
                                                                placeholder="Select Unit"
                                                                isClearable
                                                            />
                                                            <span className="text-danger">{currentProductError?.product_units?.[index]?.uom_id}</span>
                                                        </div>
                                                        {/* Conversion Factor Input */}
                                                        <div className="col-md-5 mb-3">
                                                            <label className="form-label">Conversion Factor *</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text">1 {uoms.find(u=>u.value === unit.uom_id)?.label || 'Selected Unit'} =</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.01" 
                                                                    min="0.01" 
                                                                    className="form-control"
                                                                    name="conversion_factor"
                                                                    value={unit.conversion_factor || ''}
                                                                    onChange={(e) => handleProductUnitChange(index, 'conversion_factor', e.target.value)}
                                                                    required
                                                                />
                                                                 <span className="input-group-text">{uoms.find(u=>u.value === currentProduct.base_uom_id)?.label || 'Base Units'}</span>
                                                            </div>
                                                            <span className="text-danger">{currentProductError?.product_units?.[index]?.conversion_factor}</span>
                                                        </div>
                                                         {/* Hidden ID for updates */}
                                                         <input type="hidden" name="id" value={unit.id || ''} />
                                                    </div>
                                                </div>
                                            ))}

                                            {/* --- Action Buttons --- */}
                                            <div className="col-12 mt-4">
                                                <div className="text-end">
                                                    {formType === 'create' ? (
                                                        <>
                                                            <button className="btn btn-outline-primary me-2 btn-sm" type="button" onClick={() => saveForm(false)} disabled={loading}>Save + Next</button>
                                                            <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={loading}>Save + Exit</button>
                                                            <button className="d-none d-md-inline-block btn btn-danger btn-sm me-2" type="button" onClick={handleReset} disabled={loading}>Reset</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={loading}>Update</button>
                                                            <button className="btn btn-secondary me-2 btn-sm" type="button" onClick={handleReset} disabled={loading}>Reset</button>
                                                         
                                                        </>
                                                    )}
                                                    <button className="d-none  d-md-inline-block btn btn-light-reddit b-r-22 d-inline-flex-center" type="button" onClick={() => navigate('/franchisee_user/masters/products')} disabled={loading}>Go Back</button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default ProductsForm;